import { useEffect, useState } from 'react';
import { formatFileSize, groupDocumentTypes, type DocumentTypeScope } from '@brimo/shared';
import { Banner, Button, Dialog, FileField, Select, TextField } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import type { DocumentRow } from '../../contacts/types';
import { uploadOwnerDocument, useDocumentTypes, type DocumentOwnerRef } from '../api';
import styles from './dialogs.module.css';

export interface OwnerUploadDialogProps {
  open: boolean;
  owner: DocumentOwnerRef;
  /** "Objektunterlagen" (Objekte 03), "Unterlagen zur Einheit" (Objekte 04) or "Mietverhältnis". */
  eyebrow: string;
  title: string;
  /** Preselected document type (an open checklist item's type). */
  documentTypeId?: string | undefined;
  onClose: () => void;
  onUploaded: (row: DocumentRow) => void;
}

function scopeOf(owner: DocumentOwnerRef): DocumentTypeScope {
  if ('propertyId' in owner) return 'property';
  if ('unitId' in owner) return 'unit';
  return 'tenancy';
}

/**
 * „Unterlagen hochladen" / „Unterlage hochladen": one PDF/JPG/PNG file with the party and the document
 * type → `POST /documents` with the property, unit or tenancy as owner (the document rules of api-documents.md).
 * The type picker offers the types of that record, grouped (scope and group of the taxonomy, 0032).
 */
export function OwnerUploadDialog({
  open,
  owner,
  eyebrow,
  title,
  documentTypeId,
  onClose,
  onUploaded,
}: OwnerUploadDialogProps) {
  const types = useDocumentTypes(open);
  const groups = groupDocumentTypes(types.data?.items ?? [], scopeOf(owner));
  const [file, setFile] = useState<File | null>(null);
  const [party, setParty] = useState<'broker' | 'developer' | 'internal'>('broker');
  const [typeId, setTypeId] = useState(documentTypeId ?? '');
  const [name, setName] = useState('');
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setParty('broker');
    setTypeId(documentTypeId ?? '');
    setName('');
    setFileError(undefined);
    setError(null);
    setProgress(null);
  }, [open, documentTypeId]);

  async function submit() {
    if (!file) {
      setFileError('Bitte eine Datei hochladen.');
      return;
    }
    setFileError(undefined);
    setError(null);
    setProgress(0);
    try {
      const row = await uploadOwnerDocument(
        {
          owner,
          party,
          documentTypeId: typeId || undefined,
          name: name.trim() || undefined,
          file,
        },
        { onProgress: setProgress },
      );
      onUploaded(row);
    } catch (err) {
      if (err instanceof ApiError && err.fields['file']) setFileError(err.fields['file']);
      else setError(errorMessage(err));
    } finally {
      setProgress(null);
    }
  }

  const pending = progress !== null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow={eyebrow}
      title={title}
      subtitle="PDF, JPG oder PNG bis 10 MB. Die Datei wird vor der Freigabe auf Viren geprüft."
      width={600}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            Hochladen
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <FileField
          label="Datei"
          title="Datei hierher ziehen"
          description="PDF, JPG oder PNG."
          buttonLabel="Datei auswählen"
          accept="application/pdf,image/jpeg,image/png"
          multiple={false}
          error={fileError}
          onFiles={(files) => {
            const [first] = files;
            setFile(first ?? null);
            setFileError(undefined);
          }}
          items={
            file
              ? [
                  {
                    id: 'file',
                    name: file.name,
                    size: formatFileSize(file.size),
                    ...(progress !== null ? { progress: Math.round(progress * 100) } : {}),
                  },
                ]
              : []
          }
          onRemove={pending ? undefined : () => setFile(null)}
        />
        <div className={styles.grid2}>
          <Select
            label="Partei"
            value={party}
            options={[
              { value: 'broker', label: 'Vermittler' },
              { value: 'developer', label: 'Bauträger' },
              { value: 'internal', label: 'Intern' },
            ]}
            onChange={(event) => setParty(event.target.value as 'broker' | 'developer' | 'internal')}
          />
          <Select
            label="Dokumentart"
            optional
            placeholder="Ohne Dokumentart"
            value={typeId}
            onChange={(event) => setTypeId(event.target.value)}
          >
            {groups.map((group) => (
              <optgroup key={group.group} label={group.label}>
                {group.items.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </div>
        <TextField
          label="Name"
          optional
          hint="Ohne Angabe gilt der Dateiname."
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
    </Dialog>
  );
}
